import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Clock, ArrowRight, BookOpen, PlayCircle, ShieldCheck } from "lucide-react";
import QstLayout from "./QstLayout";
import QstAssistantWidget from "./QstAssistantWidget";
import { qstTradeDetail } from "./api";

export default function QstTradeDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    qstTradeDetail(slug)
      .then((d) => { setData(d); document.title = `${d.trade.name} — QST`; })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Navigate to="/qst/trades" replace />;
  if (!data) return <QstLayout><div className="max-w-6xl mx-auto px-6 py-20 text-center text-slate-500">Loading…</div></QstLayout>;
  const { trade, lessons } = data;

  return (
    <QstLayout>
      <QstAssistantWidget context={{ page: "trade", trade: trade.slug, tradeLabel: trade.name }} />
      <section className="relative bg-[#0B2545] text-white overflow-hidden">
        {trade.hero && (
          <img src={trade.hero} alt=""
               className="absolute inset-0 w-full h-full object-cover opacity-25" />
        )}
        <div className="relative max-w-7xl mx-auto px-6 py-16">
          <Link to="/qst/trades" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#F97316] font-bold hover:underline">
            ← All Trades
          </Link>
          <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">{trade.name}</h1>
          {trade.subtitle && <p className="mt-2 text-lg text-slate-200">{trade.subtitle}</p>}
          <p className="mt-5 max-w-2xl text-slate-200 leading-relaxed">{trade.summary}</p>
          <div className="mt-7 flex flex-wrap items-center gap-5 text-sm">
            <span className="rounded-md bg-white/10 px-3 py-1.5 font-bold">{trade.lesson_count} lessons published</span>
            {trade.quality_count > 0 && <span className="rounded-md bg-orange-500/20 text-orange-100 px-3 py-1.5 font-bold">{trade.quality_count} QC lessons</span>}
            {trade.safety_count > 0 && <span className="rounded-md bg-red-500/20 text-red-100 px-3 py-1.5 font-bold">{trade.safety_count} safety lessons</span>}
            {trade.loss_count > 0 && <span className="rounded-md bg-emerald-500/20 text-emerald-100 px-3 py-1.5 font-bold">{trade.loss_count} loss-prevention</span>}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-12" data-testid="qst-trade-lessons">
        <h2 className="text-2xl font-bold text-[#0B2545]">Lessons in {trade.name}</h2>
        {lessons.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-slate-300 p-8 text-center text-slate-600">
            More lessons launching for this trade soon. <Link to="/qst/contact?intent=waitlist" className="text-[#F97316] font-bold hover:underline">Notify me →</Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {lessons.map((l) => (
              <Link key={l.slug} to={`/qst/lessons/${l.slug}`}
                className="group rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md transition"
                data-testid={`qst-lesson-card-${l.slug}`}>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                  <span className={`rounded-md px-1.5 py-0.5 ${l.category === "safety" ? "bg-red-50 text-red-700" : l.category === "loss-prevention" ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>
                    {l.category.replace('-', ' ')}
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Clock className="w-3 h-3" /> {l.estimated_minutes} min
                  </span>
                  {l.free_sample && <span className="rounded-md bg-emerald-100 text-emerald-800 px-1.5 py-0.5">FREE</span>}
                </div>
                <div className="mt-3 text-base font-bold text-[#0B2545] leading-snug">{l.title}</div>
                <div className="mt-1.5 text-xs text-slate-600 line-clamp-2 leading-relaxed">{l.objective}</div>
                <div className="mt-3 text-[11px] text-slate-500">
                  v{l.version} · reviewed {l.last_reviewed} · by {l.author}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#F97316] group-hover:translate-x-0.5 transition">
                  Open lesson <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </QstLayout>
  );
}
